import React, { useState, useRef, useCallback } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SendIcon from "@mui/icons-material/Send";
import TuneIcon from "@mui/icons-material/Tune";
import AgentAvatar from "./AgentAvatar";
import ChatMessageInput from "../TipTap/ChatMessageInput";
import { getClickableAgentAvatarSx } from "./agentSessionsPanelUtils";
import { useStatusPalette } from "../../utils/statusColors";
import useAttachments, {
  buildAttachmentBlock,
  separateAttachments,
  isVisionCapableModel,
  ACCEPT_STRING,
} from "./useAttachments";
import AttachmentStrip from "./AttachmentStrip";

export default function NewChatComposer({
  onSend,
  disabled,
  agentName,
  agentDescription,
  agentInboxAddress,
  agentId,
  agentModel,
  onConfigOpen,
  showSessionOperationalMetaInChat = true,
  welcomePanelContent = null,
  examplePrompts = [],
  hideComposerInput = false,
}) {
  const _sp = useStatusPalette();
  const [isEmpty, setIsEmpty] = useState(true);
  const [copied, setCopied] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const dragCounter = useRef(0);
  const avatarClick = onConfigOpen
    ? () => {
        onConfigOpen();
      }
    : undefined;

  const {
    attachments,
    hasAttachments,
    isUploading,
    fileInputRef,
    removeAttachment,
    clearAttachments,
    openFilePicker,
    handleFileInputChange,
    handleDrop: onDrop,
    handleDragOver,
    warning: attachmentWarning,
  } = useAttachments(agentId);

  const isVision = isVisionCapableModel(agentModel);

  const wrappedOnSend = useCallback(
    (text) => {
      if (isUploading) return;
      const { images, files } = separateAttachments(attachments, isVision);
      const block = buildAttachmentBlock(files);
      const imageUrls = images.map((a) => a.downloadUrl);
      onSend(text + block, imageUrls);
      clearAttachments();
    },
    [onSend, attachments, isUploading, clearAttachments, isVision]
  );

  const handleExamplePromptClick = useCallback((prompt) => {
    inputRef.current?.setText(prompt);
    setIsEmpty(!String(prompt || "").trim());
  }, []);

  const examplePromptCards =
    Array.isArray(examplePrompts) && examplePrompts.length > 0 ? (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
          },
          gap: 1,
          width: "100%",
        }}
      >
        {examplePrompts.map((example, index) => (
          <Box
            key={`example-prompt-${index}`}
            role="button"
            tabIndex={disabled ? -1 : 0}
            onClick={() => {
              if (!disabled) handleExamplePromptClick(example.prompt);
            }}
            onKeyDown={(event) => {
              if (disabled) return;
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleExamplePromptClick(example.prompt);
              }
            }}
            sx={{
              p: 1.5,
              border: 1,
              borderColor: "divider",
              borderRadius: "12px",
              backgroundColor: "background.paper",
              cursor: disabled ? "default" : "pointer",
              transition:
                "border-color 150ms ease, background-color 150ms ease, transform 150ms ease",
              "&:hover": disabled
                ? {}
                : {
                    borderColor: "text.secondary",
                    backgroundColor: "action.hover",
                    transform: "translateY(-1px)",
                  },
              "&:focus-visible": {
                outline: "2px solid",
                outlineColor: "text.secondary",
                outlineOffset: 2,
              },
            }}
          >
            {example.title && (
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "text.primary",
                  mb: 0.5,
                }}
              >
                {example.title}
              </Typography>
            )}
            <Typography
              sx={{
                fontSize: "13px",
                color: "text.secondary",
                lineHeight: 1.45,
              }}
            >
              {example.prompt}
            </Typography>
          </Box>
        ))}
      </Box>
    ) : null;

  const handleDropZoneDrop = useCallback(
    (e) => {
      dragCounter.current = 0;
      setDragOver(false);
      onDrop(e);
    },
    [onDrop]
  );

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (dragCounter.current === 1) setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setDragOver(false);
    }
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          maxWidth: "640px",
          px: 3,
          gap: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: "24px",
              fontWeight: 600,
              color: "text.primary",
              textAlign: "center",
              lineHeight: 1.3,
            }}
          >
            What can I help with?
          </Typography>
          {agentName && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <AgentAvatar
                name={agentName}
                size={20}
                borderRadius="4px"
                onClick={avatarClick}
              />
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "text.secondary",
                }}
              >
                {agentName}
              </Typography>
              {onConfigOpen && (
                <IconButton
                  onClick={onConfigOpen}
                  size="small"
                  sx={{
                    width: 24,
                    height: 24,
                    color: "text.secondary",
                    "&:hover": {
                      backgroundColor: "action.hover",
                      color: "text.secondary",
                    },
                  }}
                >
                  <TuneIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}
            </Box>
          )}
          {agentDescription && (
            <Typography
              sx={{
                fontSize: "13px",
                color: "text.secondary",
                textAlign: "center",
                maxWidth: 480,
                lineHeight: 1.5,
              }}
            >
              {agentDescription}
            </Typography>
          )}
          {showSessionOperationalMetaInChat && agentInboxAddress && (
            <Box
              onClick={() => {
                navigator.clipboard.writeText(agentInboxAddress);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                py: "4px",
                px: "10px",
                borderRadius: "6px",
                backgroundColor: copied ? _sp.greenAlt.bg : "action.hover",
                border: 1,
                borderColor: copied ? _sp.greenAlt.border : "divider",
                cursor: "pointer",
                transition: "all 150ms ease",
                "&:hover": {
                  backgroundColor: copied ? _sp.greenAlt.bg : "action.selected",
                },
              }}
            >
              <EmailOutlinedIcon
                sx={{
                  fontSize: 14,
                  color: copied ? _sp.greenAlt.color : "text.disabled",
                }}
              />
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: copied ? _sp.greenAlt.color : "text.secondary",
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                }}
              >
                {agentInboxAddress}
              </Typography>
              {copied && (
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 600,
                    color: _sp.greenAlt.color,
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  Copied!
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {welcomePanelContent}
        {!hideComposerInput && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPT_STRING}
              onChange={handleFileInputChange}
              style={{ display: "none" }}
            />
            <Box
              className={`composer-drop-zone${dragOver ? " drag-over" : ""}`}
              onDrop={handleDropZoneDrop}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                border: 1,
                borderColor: "divider",
                borderRadius: "12px",
                backgroundColor: "background.paper",
                transition: "border-color 150ms ease",
                "&:focus-within": { borderColor: "text.secondary" },
              }}
            >
              {hasAttachments && (
                <AttachmentStrip
                  attachments={attachments}
                  onRemove={removeAttachment}
                />
              )}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-end",
                  gap: 0.5,
                  px: 1.5,
                  py: 0.5,
                }}
              >
                <IconButton
                  onClick={openFilePicker}
                  disabled={disabled}
                  size="small"
                  sx={{
                    mb: 0.25,
                    color: "text.secondary",
                    width: 28,
                    height: 28,
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <AddIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <ChatMessageInput
                  ref={inputRef}
                  onSend={wrappedOnSend}
                  onChange={setIsEmpty}
                  placeholder={
                    disabled ? "Starting conversation..." : "Send a message..."
                  }
                  disabled={disabled}
                  autoFocus
                />
                <IconButton
                  onClick={() => {
                    if (!isEmpty) {
                      inputRef.current?.send();
                    } else if (hasAttachments && !isUploading) {
                      wrappedOnSend("");
                    }
                  }}
                  disabled={
                    (isEmpty && !hasAttachments) || disabled || isUploading
                  }
                  size="small"
                  sx={{
                    mb: 0.25,
                    color:
                      (!isEmpty || hasAttachments) && !disabled
                        ? "text.primary"
                        : "text.disabled",
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <SendIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>
            {attachmentWarning && (
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "warning.main",
                  mt: 0.5,
                  px: 0.5,
                  alignSelf: "flex-start",
                }}
              >
                {attachmentWarning}
              </Typography>
            )}
          </>
        )}
        {examplePromptCards}
      </Box>
    </Box>
  );
}
